import { useAtomValue, useSetAtom } from "jotai";
import {
  Edit3,
  Eraser,
  Kanban,
  LayoutGrid,
  Plus,
  RefreshCw,
  Trash2,
} from "@/shared/ui/icons";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { layoutAtom } from "@/core/state";
import {
  Badge,
  Button,
  Carte,
  Etiquette,
  Grille,
  Infobulle,
  Ligne,
  Lignes,
  Modal,
  ModalBody,
  ModalFooter,
} from "@/shared/ui";
import { useDialog } from "@/shell/dialog";

import {
  actionDelete,
  actionRename,
  actionSaveCurrentAsNew,
} from "./actions";
import {
  activeWorkspaceAtom,
  activeWorkspaceIdAtom,
  workspacesAtom,
} from "./atoms";
import {
  clearHistoryAtom,
  historyDepthAtom,
  resetCockpitAtom,
} from "./history";
import {
  applyTemplatePreservingPanels,
  LAYOUT_TEMPLATES,
  type LayoutTemplate,
} from "./LayoutTemplates";
import { DEFAULT_ACTIVE_WORKSPACE_ID } from "./presets";
import type { Workspace } from "./types";

/**
 * Pour les workspaces preset (id figé, name canonique anglais), affiche la
 * traduction depuis le catalogue. Pour les workspaces utilisateur custom, le
 * nom vient de l'input user — pas de traduction.
 */
function localizeWorkspaceName(
  ws: Workspace,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (ws.isPreset) {
    return t(`shell.workspaces.presets.${ws.id}`, { defaultValue: ws.name });
  }
  return ws.name;
}

/**
 * Dropdown style Windows Snap Layouts : grille de thumbnails de
 * dispositions. Click → applique le template au workspace actif (replace
 * le layout) avec auto-fill des cellules par les panneaux de demo.
 *
 * Les workspaces nommes (sauvegardes) sont une section secondaire en bas.
 * Les actions (save, rename, reset, delete, purge cache, reinit) sont
 * groupees dans un footer compact avec icones.
 */
export function WorkspaceSwitcher({
  variant = "bar",
}: {
  /** "bar" = libellé inline (BottomBar) ; "rail" = icône seule (SideRail). */
  variant?: "bar" | "rail";
} = {}) {
  const { t } = useTranslation();
  const isRail = variant === "rail";
  const workspaces = useAtomValue(workspacesAtom);
  const setWorkspaces = useSetAtom(workspacesAtom);
  const active = useAtomValue(activeWorkspaceAtom);
  const setActiveId = useSetAtom(activeWorkspaceIdAtom);
  const setLayout = useSetAtom(layoutAtom);
  const depth = useAtomValue(historyDepthAtom);
  const clearHistory = useSetAtom(clearHistoryAtom);
  const resetCockpit = useSetAtom(resetCockpitAtom);
  const dialog = useDialog();

  const [open, setOpen] = useState(false);

  const list = Object.values(workspaces).sort((a, b) => {
    if (a.isPreset !== b.isPreset) return a.isPreset ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  function applyTemplate(tpl: LayoutTemplate) {
    // Preserve les panneaux actifs en les redistribuant dans la nouvelle
    // forme, au lieu de tout remplacer par des placeholders.
    setLayout((prev) => applyTemplatePreservingPanels(prev, tpl));
    setOpen(false);
  }

  async function handleSaveAsNew() {
    const name = await dialog.prompt({
      title: t("shell.workspaces.title"),
      message: t("shell.workspaces.saveAsNewPrompt"),
    });
    if (!name?.trim()) return;
    let newId = "";
    setWorkspaces((ws) => {
      const result = actionSaveCurrentAsNew(
        ws,
        active?.id ?? DEFAULT_ACTIVE_WORKSPACE_ID,
        name.trim()
      );
      newId = result.newId;
      return result.workspaces;
    });
    if (newId) setActiveId(newId);
    setOpen(false);
  }

  async function handleRename() {
    if (!active) return;
    const name = await dialog.prompt({
      title: t("shell.workspaces.title"),
      message: t("shell.workspaces.renamePrompt"),
      initialValue: active.name,
    });
    if (!name?.trim() || name.trim() === active.name) return;
    setWorkspaces((ws) => actionRename(ws, active.id, name.trim()));
  }

  async function handleDelete() {
    if (!active || active.isPreset) return;
    const ok = await dialog.confirm({
      title: t("shell.workspaces.title"),
      message: t("shell.workspaces.deleteConfirm", { name: active.name }),
      variant: "danger",
      confirmLabel: t("common.delete"),
    });
    if (!ok) return;
    setWorkspaces((ws) => actionDelete(ws, active.id));
    setActiveId(DEFAULT_ACTIVE_WORKSPACE_ID);
    setOpen(false);
  }

  function handlePurgeHistory() {
    clearHistory();
    setOpen(false);
  }

  async function handleResetCockpit() {
    const ok = await dialog.confirm({
      title: t("shell.workspaces.title"),
      message: t("shell.workspaces.resetCockpitConfirm"),
      variant: "danger",
    });
    if (!ok) return;
    resetCockpit();
    window.location.reload();
  }

  return (
    <>
      {isRail ? (
        <Infobulle texte={t("shell.workspaces.tooltip")} cote="droite">
          <Button
            variant="ghost"
            size="lg"
            iconOnly
            className="h-9 w-9"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={t("shell.workspaces.tooltip")}
          >
            {/* Au rail, des colonnes. */}
            <Kanban className="h-[18px] w-[18px]" />
          </Button>
        </Infobulle>
      ) : (
        <Infobulle texte={t("shell.workspaces.tooltip")}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
            icon={<LayoutGrid className="h-3.5 w-3.5" />}
          >
            {active ? localizeWorkspaceName(active, t) : "—"}
          </Button>
        </Infobulle>
      )}

      {/* `Modal` : portée sur <body> (le rail et la barre ont un `backdrop-filter`, qui deviendrait
          le bloc conteneur d'un `fixed` — la fenêtre était écrasée à 48 px), Échap et le fond
          ferment, le focus entre dans la fenêtre. */}
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        size="md"
        title={t("shell.workspaces.title")}
        subtitle={t("shell.workspaces.subtitle")}
        ariaLabel={t("shell.workspaces.ariaLabel")}
        closeLabel={t("common.close")}
      >
        <ModalBody className="flex flex-col gap-5">
          {/* Les dispositions : une carte cliquable par forme, sa vignette dedans. */}
          <section className="flex flex-col gap-2">
            <Etiquette as="h3">{t("shell.workspaces.layouts")}</Etiquette>
            <Grille min={88} ecart={8}>
              {LAYOUT_TEMPLATES.map((tpl) => {
                const name = t(`shell.workspaces.templates.${tpl.id}`, { defaultValue: tpl.name });
                const cellCount =
                  tpl.cells > 1
                    ? t("shell.workspaces.cellsOther", { count: tpl.cells })
                    : t("shell.workspaces.cellsOne", { count: tpl.cells });
                const libelle = t("shell.workspaces.cellsTitle", { name, cellCount });
                return (
                  <Infobulle key={tpl.id} texte={libelle}>
                    <Carte onClick={() => applyTemplate(tpl)} remplissage="sm" aria-label={libelle}>
                      <div className="flex aspect-[4/3] flex-col items-stretch gap-0.5">{tpl.thumbnail}</div>
                    </Carte>
                  </Infobulle>
                );
              })}
            </Grille>
          </section>

          {/* Les espaces enregistrés : celui d'où l'on vient est la ligne choisie. */}
          {list.length > 0 && (
            <section className="flex flex-col gap-2">
              <Etiquette as="h3">{t("shell.workspaces.saved")}</Etiquette>
              <Lignes ariaLabel={t("shell.workspaces.saved")}>
                {list.map((ws) => (
                  <Ligne
                    key={ws.id}
                    etiquette={localizeWorkspaceName(ws, t)}
                    actif={ws.id === active?.id}
                    apres={ws.isPreset ? <Badge ton="gris">{t("shell.workspaces.preset")}</Badge> : undefined}
                    onClick={() => {
                      setActiveId(ws.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </Lignes>
            </section>
          )}
        </ModalBody>

        {/* Les gestes : des boutons fantômes, leur nom en infobulle. */}
        <ModalFooter className="justify-start gap-1">
          <FooterAction
            icon={<Plus className="h-4 w-4" />}
            label={t("shell.workspaces.saveAsNew")}
            onClick={handleSaveAsNew}
          />
          <FooterAction
            icon={<Edit3 className="h-4 w-4" />}
            label={t("shell.workspaces.rename")}
            onClick={handleRename}
            disabled={!active}
          />
          {active && !active.isPreset && (
            <FooterAction
              icon={<Trash2 className="h-4 w-4" />}
              label={t("shell.workspaces.delete")}
              onClick={handleDelete}
              danger
            />
          )}
          <FooterAction
            icon={<Eraser className="h-4 w-4" />}
            label={t("shell.workspaces.purgeHistory", { past: depth.past, future: depth.future })}
            onClick={handlePurgeHistory}
            disabled={depth.past === 0 && depth.future === 0}
          />
          <FooterAction
            icon={<RefreshCw className="h-4 w-4" />}
            label={t("shell.workspaces.resetCockpit")}
            onClick={handleResetCockpit}
            danger
          />
        </ModalFooter>
      </Modal>
    </>
  );
}

interface FooterActionProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

function FooterAction({ icon, label, onClick, disabled, danger }: FooterActionProps) {
  return (
    <Infobulle texte={label}>
      <Button
        variant="ghost"
        size="lg"
        iconOnly
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={danger ? "hover:text-erreur" : undefined}
        icon={icon}
      />
    </Infobulle>
  );
}
